import type Luminary from '@lumen5/luminary';
import type { Asset, Scene, TimeAwareMediaAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { isVoiceDrivenSceneGroup } from 'js/src/luminary/luminary-selectors';

export type MorphChainMember = {
    sceneId: string;
    assetId: string;
    duration: number;
};

export function getOrderedMorphChain(
    luminary: Readonly<Luminary>,
    focusedSceneId: string,
    focusedAssetId: string,
): MorphChainMember[] {
    const focusedScene = luminary.getSceneById(focusedSceneId);
    const focusedAsset = focusedScene?.getAssetById(focusedAssetId);
    if (!focusedScene || !focusedAsset) {
        return [];
    }

    const toMember = (scene: Scene, asset: Asset): MorphChainMember => ({
        sceneId: scene.id,
        assetId: asset.id,
        duration: scene.duration,
    });

    const isLinkedMatch = (asset: Asset) =>
        !asset.isMatchCutDuplicate && asset.isMorphMatch(focusedAsset);

    const before: MorphChainMember[] = [];
    let current = focusedScene;
    while (current.prevScene?.morphEnabled) {
        const prevScene = current.prevScene;
        const match = prevScene.getAssets().find(isLinkedMatch);
        if (!match) {
            break;
        }
        before.unshift(toMember(prevScene, match));
        current = prevScene;
    }

    const after: MorphChainMember[] = [];
    current = focusedScene;
    while (current.morphEnabled && current.nextScene) {
        const nextScene = current.nextScene;
        const match = nextScene.getAssets().find(isLinkedMatch);
        if (!match) {
            break;
        }
        after.push(toMember(nextScene, match));
        current = nextScene;
    }

    return [...before, toMember(focusedScene, focusedAsset), ...after];
}

/**
 * Walks the scene sequence from the focused asset and returns a map of sceneId → assetId
 * for all linked assets. Includes the focused asset itself only if the chain has
 * at least one other member. Each asset morphs to exactly 1 asset in an adjacent scene.
 */
export function getSceneSequenceMap(
    luminary: Readonly<Luminary>,
    focusedSceneId: string,
    focusedAssetId: string,
): Map<string, string> {
    const chain = getOrderedMorphChain(luminary, focusedSceneId, focusedAssetId);
    if (chain.length <= 1) {
        return new Map();
    }
    return new Map(chain.map(({ sceneId, assetId }) => [sceneId, assetId]));
}

export function getMorphChainHeadAsset(
    luminary: Readonly<Luminary>,
    chain: readonly MorphChainMember[],
): TimeAwareMediaAsset | undefined {
    const head = chain[0];
    if (!head) {
        return undefined;
    }
    const asset = luminary.getSceneById(head.sceneId)?.getAssetById(head.assetId);
    return asset && VideoData.isMediaAsset(asset) ? (asset as TimeAwareMediaAsset) : undefined;
}

export function isChainTrimmable(
    luminary: Readonly<Luminary>,
    chain: readonly MorphChainMember[],
): boolean {
    if (chain.length <= 1) {
        return false;
    }

    return chain.every(({ sceneId, assetId }) => {
        const scene = luminary.getSceneById(sceneId);
        const asset = scene?.getAssetById(assetId);
        if (!scene || !asset || !VideoData.isMediaAsset(asset)) {
            return false;
        }

        const sceneGroup = scene.sceneGroup;
        if (!sceneGroup || isVoiceDrivenSceneGroup(sceneGroup)) {
            return false;
        }
        const mediaAsset = asset as TimeAwareMediaAsset;
        if (mediaAsset.linkToSceneGroup) {
            return false;
        }

        if (scene.matchCutTechnique !== VideoData.MATCH_CUT_NONE) {
            return false;
        }

        if (scene.subscenes.length > 1) {
            return false;
        }

        return mediaAsset.playbackRegions.length === 1;
    });
}
